import { useTranslation } from 'react-i18next';
import type { TreeDataError } from '@/types/tree-dashboard.types';

interface ErrorDisplayProps {
  error: TreeDataError;
  onRetry?: () => void;
}

export function ErrorDisplay({ error, onRetry }: ErrorDisplayProps) {
  const { t } = useTranslation('common');

  const errorMessages: Record<TreeDataError['code'], string> = {
    TREE_NOT_FOUND: t('tree.errorDisplay.treeNotFound'),
    ACCESS_DENIED: t('tree.errorDisplay.accessDenied'),
    PERSON_NOT_FOUND: t('tree.errorDisplay.personNotFound'),
    NETWORK_ERROR: t('tree.errorDisplay.networkError'),
    UNKNOWN: t('tree.errorDisplay.unknown'),
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center">
        <div className="size-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
          <span className="material-symbols-outlined text-3xl text-error">error</span>
        </div>

        <h2 className="text-xl font-bold text-foreground mb-2">
          {errorMessages[error.code]}
        </h2>

        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {error.message}
        </p>

        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
          >
            {t('tree.errorDisplay.tryAgain')}
          </button>
        )}
      </div>
    </div>
  );
}

interface InlineErrorProps {
  error: TreeDataError;
  onRetry?: () => void;
}

export function InlineError({ error, onRetry }: InlineErrorProps) {
  const { t } = useTranslation('common');

  const errorMessages: Record<TreeDataError['code'], string> = {
    TREE_NOT_FOUND: t('tree.errorDisplay.treeNotFound'),
    ACCESS_DENIED: t('tree.errorDisplay.accessDenied'),
    PERSON_NOT_FOUND: t('tree.errorDisplay.personNotFound'),
    NETWORK_ERROR: t('tree.errorDisplay.networkError'),
    UNKNOWN: t('tree.errorDisplay.unknown'),
  };

  return (
    <div className="rounded-lg border border-error/20 bg-error/5 p-4">
      <div className="flex items-start gap-3">
        <span className="material-symbols-outlined text-error">error</span>
        <div className="flex-1">
          <h3 className="font-semibold text-error mb-1">
            {errorMessages[error.code]}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {error.message}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 text-sm font-medium text-primary hover:underline"
            >
              {t('tree.errorDisplay.tryAgain')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
